import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Root container variants
 */
export const inputContainerVariants = cva('flex flex-col w-full', {
  variants: {
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
      false: '',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

/**
 * Label variants
 */
export const inputLabelVariants = cva('flex items-end gap-xxxs text-foreground font-normal', {
  variants: {
    size: {
      s: 'text-[length:var(--text-size--2)] leading-[16px] pb-xxs',
      m: 'text-[length:var(--text-size--1)] leading-[16px] pb-sm',
      l: 'text-[length:var(--text-size-0)] leading-[20px] pb-sm',
    },
    disabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-default',
    },
  },
  defaultVariants: {
    size: 'm',
    disabled: false,
  },
});

/**
 * Required indicator variants
 */
export const inputRequiredVariants = cva('text-status-danger', {
  variants: {
    size: {
      s: 'text-[length:var(--text-size--2)] leading-[16px]',
      m: 'text-[length:var(--text-size--1)] leading-[16px]',
      l: 'text-[length:var(--text-size-0)] leading-[20px]',
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

/**
 * Input wrapper (contains all input-related elements)
 */
export const inputWrapperVariants = cva('flex items-center w-full', {
  variants: {
    size: {
      s: 'h-[24px]',
      m: 'h-[32px]',
      l: 'h-[44px]',
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

/**
 * Content container (wraps input field + icons)
 */
export const inputContentContainerVariants = cva(
  'relative flex items-center flex-1 bg-surface border border-solid rounded-sm overflow-hidden transition-colors',
  {
    variants: {
      size: {
        s: 'h-[24px] gap-sm px-sm',
        m: 'h-[32px] gap-md px-md',
        l: 'h-[44px] gap-lg px-lg',
      },
      hasError: {
        true: 'border-status-danger',
        false:
          'border-outline-input hover:border-outline focus-within:border-brand focus-within:ring-2 focus-within:ring-brand',
      },
      readOnly: {
        true: 'bg-background cursor-default',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
    },
    compoundVariants: [
      {
        hasError: false,
        readOnly: true,
        className: 'border-outline-input hover:border-outline-input',
      },
      {
        hasError: false,
        disabled: true,
        className: 'border-outline-input hover:border-outline-input',
      },
    ],
    defaultVariants: {
      size: 'm',
      hasError: false,
      readOnly: false,
      disabled: false,
    },
  }
);

/**
 * Input field variants
 */
export const inputFieldVariants = cva(
  'flex-1 w-full bg-transparent border-0 outline-none text-foreground placeholder:text-foreground-muted',
  {
    variants: {
      size: {
        s: 'text-[length:var(--text-size--2)] leading-[16px] h-[24px]',
        m: 'text-[length:var(--text-size--1)] leading-[16px] h-[32px]',
        l: 'text-[length:var(--text-size-0)] leading-[20px] h-[44px]',
      },
      readOnly: {
        true: 'cursor-default',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      size: 'm',
      readOnly: false,
      disabled: false,
    },
  }
);

/**
 * Prefix text variants: a box of its own, set 1px off the field
 */
export const inputPrefixVariants = cva(
  'flex items-center me-px bg-background border border-solid border-outline-input rounded-sm text-foreground-secondary text-center whitespace-nowrap',
  {
    variants: {
      size: {
        s: 'h-[24px] px-sm text-[length:var(--text-size--2)] leading-[16px]',
        m: 'h-[32px] px-md text-[length:var(--text-size--1)] leading-[16px]',
        l: 'h-[44px] px-lg text-[length:var(--text-size-0)] leading-[20px]',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  }
);

/**
 * Suffix text variants: a box of its own, set 2px off the field
 */
export const inputSuffixVariants = cva(
  'flex items-center ms-xxxs bg-background border border-solid border-outline-input rounded-sm text-foreground-secondary text-center whitespace-nowrap overflow-hidden',
  {
    variants: {
      size: {
        s: 'h-[24px] px-sm text-[length:var(--text-size--2)] leading-[16px]',
        m: 'h-[32px] px-md text-[length:var(--text-size--1)] leading-[16px]',
        l: 'h-[44px] px-lg text-[length:var(--text-size-0)] leading-[20px]',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  }
);

/**
 * Icon wrapper variants
 */
export const inputIconVariants = cva('flex items-center justify-center shrink-0', {
  variants: {
    size: {
      s: 'size-[16px]',
      m: 'size-[16px]',
      l: 'size-[24px]',
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

/**
 * Clear button variants
 */
export const inputClearButtonVariants = cva(
  'flex items-center justify-center shrink-0 text-foreground-secondary hover:text-foreground transition-colors cursor-pointer border-0 bg-transparent outline-none',
  {
    variants: {
      size: {
        s: 'size-[16px]',
        m: 'size-[16px]',
        l: 'size-[24px]',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  }
);

/**
 * Helper text / error message variants
 */
export const inputHelperTextVariants = cva('flex items-center gap-sm pt-sm', {
  variants: {
    error: {
      true: 'text-status-danger',
      false: 'text-foreground',
    },
    size: {
      s: 'text-[length:var(--text-size--2)] leading-[18px]',
      m: 'text-[length:var(--text-size--2)] leading-[18px]',
      l: 'text-[length:var(--text-size--2)] leading-[18px]',
    },
  },
  defaultVariants: {
    error: false,
    size: 'm',
  },
});

/**
 * Helper text icon variants
 */
export const inputHelperIconVariants = cva('flex items-center justify-center shrink-0 size-[16px]');

/**
 * Loading indicator variants
 */
export const inputLoadingIndicatorVariants = cva(
  'absolute top-0 left-[-1px] right-[-1px] flex h-[2px]',
  {
    variants: {
      loading: {
        true: 'opacity-100',
        false: 'opacity-0 pointer-events-none',
      },
    },
    defaultVariants: {
      loading: false,
    },
  }
);

/**
 * Loading track variants
 */
export const inputLoadingTrackVariants = cva('flex-1 h-full', {
  variants: {
    active: {
      true: 'bg-brand',
      false: 'bg-outline-subtle',
    },
  },
  defaultVariants: {
    active: false,
  },
});

export type InputContainerVariants = VariantProps<typeof inputContainerVariants>;
export type InputLabelVariants = VariantProps<typeof inputLabelVariants>;
export type InputRequiredVariants = VariantProps<typeof inputRequiredVariants>;
export type InputWrapperVariants = VariantProps<typeof inputWrapperVariants>;
export type InputContentContainerVariants = VariantProps<typeof inputContentContainerVariants>;
export type InputFieldVariants = VariantProps<typeof inputFieldVariants>;
export type InputPrefixVariants = VariantProps<typeof inputPrefixVariants>;
export type InputSuffixVariants = VariantProps<typeof inputSuffixVariants>;
export type InputIconVariants = VariantProps<typeof inputIconVariants>;
export type InputClearButtonVariants = VariantProps<typeof inputClearButtonVariants>;
export type InputHelperTextVariants = VariantProps<typeof inputHelperTextVariants>;
export type InputHelperIconVariants = VariantProps<typeof inputHelperIconVariants>;
export type InputLoadingIndicatorVariants = VariantProps<typeof inputLoadingIndicatorVariants>;
export type InputLoadingTrackVariants = VariantProps<typeof inputLoadingTrackVariants>;
